import { acceptHMRUpdate, defineStore } from "pinia";

function defineBooleanList<T extends { [index: string]: boolean }>(
  i: T
): { [k in keyof T]: boolean } {
  return i;
}

const defaultDrawerStates = defineBooleanList({
  // model
  relationDescription: true,
  relationCurve: true,
  relationDelay: false,
  relationInterval: false,
  relationDesign: false,
  
  modelInfo: false,
  modelDesign: false,

  elements: true,
  targets: true,
  resources: true,
  indices: true,
  
  stakeholders: false,
  question: false,
  template: false,

  // stakeholder submenu
  stakeholderGeneral: true,
  stakeholderIndices: false,
  stakeholderTargets: false,
  stakeholderActions: false,
  stakeholderResources: false,

  // target menu
  targetGeneral: true,
  targetFlows: false,
  targetIndicators: false,
  targetStatus: false,
  targetImpacts: false,
  targetMomentum: false,
  targetStakeholders: false,
  targetDesign: false,

  // index menu
  indexGeneral: true,
  indexValue: false,
  indexRelations: false,
  indexStakeholders: false,
  indexDesign: false,

  momentumGeneral: true,
  momentumCurve: false,

  // flow menu
  flowGeneral: false,
  flowValue: true,

  // evaluation
  simulationInfo: false,
  evaluationDataSettings: false,
  evaluationViewSettings: false,

  // inspector
  waysAndCycles: true,
  inspectorImpulseTracking: false,

  // variables
  resourceGeneral: true,
  resourceRelations: false,
  resourceFlows: false,
  resourceState: false,
  resourceMomentum: false,
  resourceStakeholders: false,
  resourceDesign: false,

  incidentGeneral: true,
  incidentEffects: false,
  incidentTriggers: false,
  incidentDependingIncidents: false,
  incidentScenarios: false,
  incidentDesign: false,
  incidentVisibility: false,

  actionGeneral: true,
  actionCosts: false,
  actionConditions: false,
  actionRequirements: false,
  actionEffects: false,
  actionVisibility: false,
  actionDependingIncidents: false,
  actionStakeholders: false,
  actionScenarios: false,
  actionDesign: false,

  scenarioGeneral: true,
  scenarioStartStates: false,
  scenarioIncidents: false,
  scenarioActions: false,
  scenarioEnds: false,
  scenarioStart: false,
  scenarioEnd: false,
  scenarioVisibility: false,

  triggerGeneral: false,
  triggerConditions: true,

  requirementGeneral: false,
  requirementConditions: true,

  effectOverRounds: false,
  effectVisibility: false,

  textDesign: false,
  textAdvanced: false,

  mediumDesign: true,

  shapeDesign: true,

  netChartAxes: true,
  netChartSimulations: true,
  netChartData: true,
  netChartDesign: true
});

type DrawerKey = keyof typeof defaultDrawerStates;

export const useDrawerStates = defineStore("model/drawerStates", {
  state: () => ({ ...defaultDrawerStates }),
  getters: {},
  actions: {
    open(entry: DrawerKey) {
      this.$patch((state) => {
        state[entry] = true;
      });
    },
    close(entry: DrawerKey) {
      this.$patch((state) => {
        state[entry] = false;
      });
    },
    toggle(entry: DrawerKey) {
      this.$patch((state) => {
        if (state[entry]) {
          state[entry] = false;
        } else {
          state[entry] = true;
        }
      });
    },
    set(entryObject: Partial<typeof defaultDrawerStates>) {
      let entry: DrawerKey;
      for (entry in entryObject) {
        const open = entryObject[entry];
        if (open) this.open(entry);
        else this.close(entry);
      }
    },
    setOpen(drawerName: DrawerKey, openState: boolean) {
      this.$patch((state) => {
        state[drawerName] = openState;
      });
    }
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    acceptHMRUpdate(useDrawerStates as any, import.meta.hot)
  );
}
